/**
 * Flamegraph Layout Utilities
 *
 * Functions for transforming hierarchical profile data into positioned rectangles
 * for flamegraph visualization with root-at-bottom layout.
 */

import type { DiffNode } from './flamegraphDiff';

export interface ProfileNode {
    name: string;
    value: number;
    exclusive: number;
    id: number;
    parentId: number;
    children?: ProfileNode[];
    tooltip?: string;
    diffData?: DiffNode;  // Stores diff information when in diff mode
}

export interface PositionedRect {
    node: ProfileNode;
    x: number;
    y: number;
    width: number;
    height: number;
    depth: number;
    path: string;
}

export interface LayoutConfig {
    containerWidth: number;
    containerHeight: number;
    rowHeight: number;
    minNodeWidth: number;  // Minimum width to display text
}

/**
 * Calculate positioned rectangles for all nodes (root at bottom).
 *
 * Parent x/width/value are passed down through recursion so we never need to
 * search back up the tree — avoids the O(n²) findParentNode calls that made
 * this function unusable on profiles with tens of thousands of nodes.
 */
export function calculateLayout(
    root: ProfileNode,
    config: LayoutConfig
): PositionedRect[] {
    const positions: PositionedRect[] = [];

    function visit(
        node: ProfileNode,
        depth: number,
        path: string,
        parentX: number,
        parentWidth: number,
        parentValue: number,
        siblingOffset: number
    ) {
        let x: number;
        let width: number;

        if (depth === 0) {
            x = 0;
            width = config.containerWidth;
        } else {
            width = parentValue > 0 ? (node.value / parentValue) * parentWidth : 0;
            x = parentX + siblingOffset;
        }

        if (!isFinite(x)) x = 0;
        if (!isFinite(width)) width = 0;
        if (width < 0) width = 0;

        const y = config.containerHeight - ((depth + 1) * config.rowHeight);

        positions.push({ node, x, y, width, height: config.rowHeight, depth, path });

        if (node.children && node.children.length > 0) {
            const nodeName = node.name || 'root';
            const newPath = path ? `${path} > ${nodeName}` : nodeName;
            const nodeValue = node.value || 1;
            let childOffset = 0;

            for (const child of node.children) {
                visit(child, depth + 1, newPath, x, width, nodeValue, childOffset);
                childOffset += nodeValue > 0 ? (child.value / nodeValue) * width : 0;
            }
        }
    }

    visit(root, 0, '', 0, config.containerWidth, root.value || config.containerWidth, 0);

    return positions;
}

/**
 * Format node name with intelligent truncation
 *
 * @param name - Full node name
 * @param showMethodFirst - If true, show only class and method (hide namespace)
 * @returns Formatted name
 */
export function formatNodeName(name: string, showMethodFirst: boolean): string {
    if (!showMethodFirst || !name) return name;

    // First, remove generic parameters to avoid confusion with '!' inside them
    // Handle both <...> and [...] for generic parameters
    let nameWithoutGenerics = name;
    let depth = 0;
    let result = '';

    for (let i = 0; i < name.length; i++) {
        const char = name[i];

        // Track depth for both < > and [ ]
        if (char === '<' || char === '[') {
            depth++;
        } else if (char === '>' || char === ']') {
            depth--;
        } else if (depth === 0) {
            // Only include characters outside of generic brackets
            result += char;
        }
    }
    nameWithoutGenerics = result;

    // Now find the '!' separator (should be the main one, not inside generics)
    const exclamationIndex = nameWithoutGenerics.indexOf('!');

    if (exclamationIndex === -1) {
        // No '!' separator, just take the last two parts separated by '.'
        const parts = nameWithoutGenerics.split('.');
        if (parts.length >= 2) {
            return parts[parts.length - 2] + '.' + parts[parts.length - 1];
        }
        return nameWithoutGenerics; // Return as-is if can't parse
    }

    // Split on '!' to get class part and method part
    const beforeExclamation = nameWithoutGenerics.substring(0, exclamationIndex);
    const afterExclamation = nameWithoutGenerics.substring(exclamationIndex + 1);

    // Get the class name (last part before '!')
    const classParts = beforeExclamation.split('.');
    let className = classParts[classParts.length - 1];

    // If class name is a file extension (dll, exe, obj, lib, so, dylib), it's actually a file, not a class
    // Show the file name instead (e.g., "SharePoint.dll" or "MyApp.exe" instead of just "dll"/"exe")
    const fileExtensions = ['dll', 'exe', 'obj', 'lib', 'so', 'dylib'];
    const lowerClassName = className.toLowerCase();
    if (fileExtensions.includes(lowerClassName) && classParts.length >= 2) {
        className = classParts[classParts.length - 2] + '.' + className;
    }

    // Return class!method
    return className + '!' + afterExclamation;
}

/**
 * Get color for a node based on value intensity
 */
export function getNodeColor(value: number, maxValue: number, colorPalette: string[]): string {
    const ratio = maxValue > 0 ? value / maxValue : 0;
    const index = Math.min(
        Math.floor(ratio * (colorPalette.length - 1)),
        colorPalette.length - 1
    );
    return colorPalette[index];
}

/**
 * Orange to dark red color palette for traditional flamegraphs
 */
export const FLAMEGRAPH_COLORS = [
    '#FFA500', '#FF9A00', '#FF8F00', '#FF8400', '#FF7900',
    '#FF6E00', '#FF6300', '#FF5800', '#FF4D00', '#FF4200',
    '#FF3700', '#FF2C00', '#FF2100', '#FF1600', '#FF0B00',
    '#FF0000', '#F50000', '#EB0000', '#E10000', '#D70000',
    '#CD0000', '#C30000', '#B90000', '#AF0000', '#A50000'
];

/**
 * Diff mode color palettes
 */
export const DIFF_COLORS = {
    increased: ['#FFCDD2', '#EF9A9A', '#E57373', '#EF5350', '#F44336', '#E53935', '#D32F2F', '#C62828', '#B71C1C'], // Light to dark red
    decreased: ['#C8E6C9', '#A5D6A7', '#81C784', '#66BB6A', '#4CAF50', '#43A047', '#388E3C', '#2E7D32', '#1B5E20'], // Light to dark green
    unchanged: '#BDBDBD', // Gray
    new: '#CE93D8', // Purple
    removed: '#90A4AE'  // Blue-gray
};
